import React from 'react';
import { IconButton, Stack, Typography, styled } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { radius } from 'themes/radius';

// Figma: tertiary-colored rounded square, 23px on mobile and 33px from tablet up.
const NavButton = styled(IconButton)(({ theme }) => ({
  width: 23,
  height: 23,
  padding: 0,
  borderRadius: radius.card,
  backgroundColor: theme.palette.brandBackgroundSecondary.main,
  color: theme.palette.brandShade1.main,
  '&:hover, &:active, &.Mui-focusVisible, &.Mui-disabled': {
    backgroundColor: theme.palette.brandBackgroundSecondary.main,
  },
  [theme.breakpoints.up('sm')]: {
    width: 33,
    height: 33,
  },
}));

const sizedIcon = (Icon: typeof ChevronLeftIcon) =>
  styled(Icon)(({ theme }) => ({
    fontSize: 16,
    [theme.breakpoints.up('sm')]: { fontSize: 24 },
  }));

const PreviousIcon = sizedIcon(ChevronLeftIcon);
const NextIcon = sizedIcon(ChevronRightIcon);

const Root = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(2.5),
    marginBottom: theme.spacing(4),
  },
}));

const RangeLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[800],
  ...theme.typography.footnote,
  [theme.breakpoints.up('sm')]: { ...theme.typography.title3 },
}));

export interface DateRangePickerProps {
  /** Pre-formatted label, e.g. "Apr 27 - May 03, 2026". */
  label: string;
  onPrevious: () => void;
  onNext: () => void;
  disablePrevious?: boolean;
  disableNext?: boolean;
  previousAriaLabel?: string;
  nextAriaLabel?: string;
  className?: string;
}

/** Previous / next stepper around a date range label, shared by the widget detail pages. */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  onPrevious,
  onNext,
  disablePrevious = false,
  disableNext = false,
  previousAriaLabel = 'Previous week',
  nextAriaLabel = 'Next week',
  className,
}) => (
  <Root className={className}>
    <NavButton onClick={onPrevious} disabled={disablePrevious} aria-label={previousAriaLabel}>
      <PreviousIcon />
    </NavButton>
    <RangeLabel>{label}</RangeLabel>
    <NavButton onClick={onNext} disabled={disableNext} aria-label={nextAriaLabel}>
      <NextIcon />
    </NavButton>
  </Root>
);
